import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firebaseStorage, firestore, isFirebaseConfigured } from "@/lib/firebase";

const appsCollection = () => collection(firestore, "apps");

function normalizeApp(snapshot) {
  const data = snapshot.data();
  const created = data.createdAt?.toDate
    ? data.createdAt.toDate().toISOString()
    : data.created_at || new Date().toISOString();

  return {
    app_id: data.app_id || data.appId || snapshot.id,
    title: data.title || "",
    short_description: data.short_description || data.shortDescription || "",
    description: data.description || "",
    category: data.category || "Tools",
    price_mmk: Number(data.price_mmk ?? data.priceMmk ?? 0),
    version: data.version || "1.0.0",
    size_mb: Number(data.size_mb ?? data.sizeMb ?? 0),
    platform: data.platform || "android",
    developer: data.developer || "",
    icon_url: data.icon_url || data.iconUrl || "",
    file_url: data.file_url || data.fileUrl || "",
    file_path: data.file_path || data.filePath || "",
    featured: Boolean(data.featured),
    rating: Number(data.rating || 4.8),
    downloads: Number(data.downloads || 0),
    created_at: created,
  };
}

export async function listFirebaseApps() {
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured");
  const result = await getDocs(appsCollection());
  return result.docs.map(normalizeApp).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

export async function createFirebaseApp({ app, iconFile, appFile }) {
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured");
  const isFreeDownload =
    Number(app.price_mmk || 0) === 0 ||
    /free|lite/i.test(app.title || "");

  const draft = await addDoc(appsCollection(), {
    app_id: "",
    title: app.title,
    short_description: app.short_description,
    description: app.description,
    category: app.category,
    price_mmk: Number(app.price_mmk || 0),
    version: app.version,
    size_mb: Number(app.size_mb || 0),
    platform: app.platform,
    developer: app.developer,
    icon_url: app.icon_url || "",
    file_url: "",
    file_path: "",
    featured: Boolean(app.featured),
    rating: 4.8,
    downloads: 0,
    source: "firebase",
    createdAt: serverTimestamp(),
  });

  const updates = {
    app_id: draft.id,
    appId: draft.id,
    updatedAt: serverTimestamp(),
  };

  if (iconFile) {
    const extension = iconFile.name?.split(".").pop() || "png";
    const iconPath = `app-icons/${draft.id}/icon.${extension}`;
    const iconRef = ref(firebaseStorage, iconPath);
    await uploadBytes(iconRef, iconFile, { contentType: iconFile.type || "image/png" });
    updates.icon_url = await getDownloadURL(iconRef);
    updates.icon_path = iconPath;
  }

  if (appFile) {
    const extension = appFile.name?.split(".").pop() || "apk";
    const fileFolder = isFreeDownload ? "free-app-files" : "premium-app-files";
    const filePath = `${fileFolder}/${draft.id}/${appFile.name || `app.${extension}`}`;
    const fileRef = ref(firebaseStorage, filePath);
    await uploadBytes(fileRef, appFile, { contentType: appFile.type || "application/octet-stream" });
    updates.file_url = await getDownloadURL(fileRef);
    updates.file_path = filePath;
  }

  await updateDoc(doc(firestore, "apps", draft.id), updates);
  return draft.id;
}

export async function deleteFirebaseApp(appId) {
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured");
  await deleteDoc(doc(firestore, "apps", appId));
}
